import React, { useEffect, useState } from "react";
import {
  Modal,
  Loader,
  Dimmer,
  Segment,
  Table,
  Button,
  Header,
  Grid,
  Icon,
} from "semantic-ui-react";
import { normalizeId } from "../../../../../../formatting/inventoryIds";
import axios from "../../../../../../apis/dvdlocator";

import MergeStep from "./MergeStep";
import ReviewStep from "./ReviewStep";
import WizardStepsHeader, { VIEW_MODES } from "./WizardStepsHeader";
import CompletedStep from "./CompletedStep";

const MergeInventory = ({ open, onClose, inventoryIds }) => {
  const WIZARD_MODES = {
    NONE: "none",
    MERGE: "merge",
    REVIEW: "review",
    COMPLETED: "completed",
  };
  const [isLoading, setIsLoading] = useState(false);
  const [wizardMode, setWizardMode] = useState(WIZARD_MODES.NONE);
  const [inventories, setInventories] = useState([]);
  const [allSupplyUnits, setAllSupplyUnits] = useState([]);
  const [hasPartNumberMismatch, setHasPartNumberMismatch] = useState(false);
  const [mergedInventory, setMergedInventory] = useState({
    inventory_id: "",
    inventory_title: "",
    part_number: "",
    shelf: "",
    inv: 0,
  });

  const someUnitsMissingInventory = inventoryIds.some((id) => id === null);

  const loadInventoryData = async (inventoryId) => {
    try {
      const res = await axios.get(`/supply?inventoryId=${inventoryId}`);

      if (res.data && res.data.length > 0) {
        const inventory = {
          inventory_title: res.data[0].inventory_title,
          inventory_id: res.data[0].inventory_id,
          inv: res.data[0].inv,
          shelf: res.data[0].inventory_shelf,
          part_number: res.data[0].part_number,
        };

        const formattedSupplyUnits = res.data.map((unit) => ({
          upc: unit.upc,
          title: unit.supply_title,
          inv: unit.inv || "",
          inventory_id: unit.inventory_id,
          assign_new_inventory: unit.assign_new_inventory || false,
          new_inventory_title: unit.new_inventory_title || false,
          new_inventory_shelf: unit.new_inventory_shelf || false,
        }));

        return { inventory, supplyUnits: formattedSupplyUnits };
      }

      return { inventory: null, supplyUnits: [] };
    } catch (error) {
      console.error(
        `Error fetching inventory data for ID ${inventoryId}:`,
        error
      );
      throw error;
    }
  };

  const checkPartNumberMismatch = (inventories) => {
    if (!inventories || inventories.length <= 1) {
      return false;
    }

    const firstPartNumber = inventories[0].part_number;
    return inventories.some(
      (inventory) => inventory.part_number !== firstPartNumber
    );
  };

  useEffect(() => {
    if (open && inventoryIds && inventoryIds.length > 0) {
      const fetchAllInventoryData = async () => {
        setIsLoading(true);

        try {
          const allInventories = [];
          const allSupplyUnits = [];

          for (const inventoryId of inventoryIds) {
            const { inventory, supplyUnits } = await loadInventoryData(
              inventoryId
            );

            if (inventory) {
              allInventories.push(inventory);
              allSupplyUnits.push(...supplyUnits);
            }
          }

          const partNumberMismatch = checkPartNumberMismatch(allInventories);
          setHasPartNumberMismatch(partNumberMismatch);

          const totalInv = allInventories.reduce(
            (total, inv) => total + (Number(inv.inv) || 0),
            0
          );

          if (allInventories.length > 0) {
            // Set the first inventory as the default selected
            setMergedInventory({
              inventory_id: allInventories[0].inventory_id,
              inventory_title: allInventories[0].inventory_title,
              part_number: allInventories[0].part_number,
              shelf: allInventories[0].shelf,
              inv: totalInv,
            });
          }

          setInventories(allInventories);
          setAllSupplyUnits(allSupplyUnits);
        } catch (error) {
          console.error("Error fetching inventory data:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchAllInventoryData();
    }
  }, [open, inventoryIds]);

  const startWizard = () => {
    setWizardMode(WIZARD_MODES.MERGE);
  };

  const handleSave = () => {
    setWizardMode(WIZARD_MODES.COMPLETED);
  };

  const getHeaderText = () => {
    switch (wizardMode) {
      case WIZARD_MODES.NONE:
        return "Merge Inventory";
      case WIZARD_MODES.MERGE:
        return "Merge Inventory Items";
      case WIZARD_MODES.REVIEW:
        return "Review Merge Changes";
      case WIZARD_MODES.COMPLETED:
        return "Inventory Merge Complete";
      default:
        return "Merge Inventory";
    }
  };

  const getHeaderDescription = () => {
    switch (wizardMode) {
      case WIZARD_MODES.NONE:
        return "Below are all inventories to be merged and their supply units";
      case WIZARD_MODES.MERGE:
        return "Select how to merge these inventory records";
      case WIZARD_MODES.REVIEW:
        return "Review your merge changes before submission";
      case WIZARD_MODES.COMPLETED:
        return "Your inventory merge has been processed successfully";
      default:
        return "";
    }
  };

  const renderInventorySection = (inventory) => {
    const inventorySupplyUnits = allSupplyUnits.filter(
      (unit) => unit.inventory_id === inventory.inventory_id
    );

    const isSelected = mergedInventory.inventory_id === inventory.inventory_id;

    const handleSelectInventory = () => {
      // Update merged inventory with the selected inventory's details
      // but keep the total inventory count
      const totalInv = inventories.reduce(
        (total, inv) => total + (Number(inv.inv) || 0),
        0
      );

      setMergedInventory({
        inventory_id: inventory.inventory_id,
        inventory_title: inventory.inventory_title,
        part_number: inventory.part_number,
        shelf: inventory.shelf,
        inv: totalInv,
      });
    };

    return (
      <Segment
        secondary={!isSelected}
        color={isSelected ? "blue" : null}
        style={{
          marginBottom: "40px",
          marginTop: "40px",
          cursor: "pointer",
          borderLeft: isSelected ? "4px solid #2185d0" : null,
        }}
        key={inventory.inventory_id}
        onClick={handleSelectInventory}
      >
        <p>
          {isSelected && (
            <span style={{ marginLeft: "10px", color: "#2185d0" }}>
              <Icon name="check circle" /> Selected as merge target
            </span>
          )}
        </p>
        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <Header as="h3">{inventory.inventory_title}</Header>
              <p>Shelf: {inventory.shelf}</p>
              <p>Current Inv: {inventory.inv}</p>
            </Grid.Column>
            <Grid.Column width={8}>
              <Header as="h3">
                Inventory ID: {normalizeId(inventory.inventory_id)}
              </Header>
              <p>Component #: {inventory.part_number}</p>
              <p>{inventorySupplyUnits.length} equivalent UPCs</p>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>UPC</Table.HeaderCell>
              <Table.HeaderCell>Title</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {inventorySupplyUnits.map((unit, index) => (
              <Table.Row key={`${unit.upc}-${index}`}>
                <Table.Cell>{unit.upc}</Table.Cell>
                <Table.Cell>{unit.title}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>
    );
  };

  const renderReadMode = () => {
    return (
      <div>
        <Segment style={{ textAlign: "center", marginBottom: "20px" }}>
          <Icon name="info circle" />
          Click on an inventory below to select it as your merge target. All
          other inventories will be merged into this one.
        </Segment>

        {inventories.map((inventory) => renderInventorySection(inventory))}

        {someUnitsMissingInventory && (
          <Segment
            color="orange"
            inverted
            style={{ textAlign: "center", marginBottom: "20px" }}
          >
            <Icon name="exclamation triangle" />
            <p>Not all selected units have an inventory record. </p>

            <p>
              Please remove from selection or create an inventory record for
              them first
            </p>

            <p>
              Inventory records can be created by right-clicking a row and going
              to Inventory {">"} Inventory Details
            </p>
          </Segment>
        )}

        {hasPartNumberMismatch && (
          <Segment
            color="orange"
            inverted
            style={{ textAlign: "center", marginBottom: "20px" }}
          >
            <Icon name="warning sign" />
            Cannot merge inventory that have different part numbers{" "}
            {`(Component #)`}
          </Segment>
        )}

        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <Button
            color="blue"
            onClick={startWizard}
            disabled={hasPartNumberMismatch || someUnitsMissingInventory}
          >
            Start Merge Process with Selected Inventory
            <Icon name="arrow right" style={{ marginLeft: "10px" }} />
          </Button>
        </div>
      </div>
    );
  };

  const renderWizardContent = () => {
    switch (wizardMode) {
      case WIZARD_MODES.MERGE:
        return (
          <MergeStep
            inventories={inventories}
            supplyUnits={allSupplyUnits}
            mergedInventory={mergedInventory}
            setMergedInventory={setMergedInventory}
            onStepComplete={() => {
              setWizardMode(WIZARD_MODES.REVIEW);
            }}
            onPrevious={() => setWizardMode(WIZARD_MODES.NONE)}
          />
        );

      case WIZARD_MODES.REVIEW:
        return (
          <ReviewStep
            mergedInventory={mergedInventory}
            inventories={inventories}
            supplyUnits={allSupplyUnits}
            onStepComplete={() => {
              handleSave();
            }}
            onPrevious={() => setWizardMode(WIZARD_MODES.MERGE)}
          />
        );

      case WIZARD_MODES.COMPLETED:
        return (
          <CompletedStep
            mergedInventory={mergedInventory}
            supplyUnits={allSupplyUnits}
            onFinish={onClose}
          />
        );

      default:
        return null;
    }
  };

  const getViewModeForWizard = () => {
    switch (wizardMode) {
      case WIZARD_MODES.MERGE:
        return VIEW_MODES.MERGE;
      case WIZARD_MODES.REVIEW:
        return VIEW_MODES.REVIEW;
      case WIZARD_MODES.COMPLETED:
        return VIEW_MODES.COMPLETED;
      default:
        return VIEW_MODES.READ;
    }
  };

  if (isLoading) {
    return (
      <Modal open={open} onClose={onClose} closeOnDimmerClick size="large">
        <Modal.Header>{getHeaderText()}</Modal.Header>
        <Modal.Content>
          <Segment style={{ height: "200px" }}>
            <Dimmer active inverted>
              <Loader inverted>Loading Inventory Data</Loader>
            </Dimmer>
          </Segment>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose} disabled={isLoading}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} closeOnDimmerClick size="large">
      <Modal.Header>
        {getHeaderText()}
        <div
          style={{
            fontSize: "0.7em",
            marginTop: "5px",
            fontWeight: "normal",
          }}
        >
          {getHeaderDescription()}
        </div>
      </Modal.Header>
      <Modal.Content>
        {wizardMode === WIZARD_MODES.NONE ? (
          renderReadMode()
        ) : (
          <>
            <WizardStepsHeader
              viewMode={getViewModeForWizard()}
              isLoading={isLoading}
            />
            {renderWizardContent()}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isLoading}>
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default MergeInventory;
